import { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { teamMemberSchema } from "../schemas/teamMemberSchema";
import { teamMemberService } from "../../../services/teamMemberService";
import { toast } from "../../../services/toastService";
import { getCroppedImageBlob } from "../utils/cropImage";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const TeamMemberForm = ({ getAsync, id, setId }) => {
  const isEditMode = Boolean(id);

  const initialForm = {
    name: "",
    position: "",
    displayOrder: "",
    isPublished: true,
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(teamMemberSchema),
    defaultValues: initialForm,
  });

  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [selectedFileSrc, setSelectedFileSrc] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedBlob, setCroppedBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageError, setImageError] = useState("");

  useEffect(() => {
    if (id > 0) {
      editAsync();
    } else {
      resetImageState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const editAsync = async () => {
    try {
      const result = await teamMemberService.edit(id);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to load team member");
        return;
      }
      reset({
        name: result.value.name,
        position: result.value.position,
        displayOrder: result.value.displayOrder,
        isPublished: result.value.isPublished,
      });
      setExistingImageUrl(result.value.imagePath);
      setSelectedFileSrc(null);
      setCroppedBlob(null);
      setPreviewUrl(null);
      setShowCropper(false);
    } catch (error) {
      toast.error("An error occurred while loading the team member");
      console.log(error);
    }
  };

  const resetImageState = () => {
    setExistingImageUrl(null);
    setSelectedFileSrc(null);
    setCroppedBlob(null);
    setPreviewUrl(null);
    setShowCropper(false);
    setImageError("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setImageError("");

    if (!ALLOWED_TYPES.includes(file.type)) {
      setImageError("Please choose a JPG, PNG, or WEBP image");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setImageError("Image must not exceed 5 MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setSelectedFileSrc(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = (_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  };

  const applyCrop = async () => {
    try {
      if (!croppedAreaPixels) return;
      const blob = await getCroppedImageBlob(selectedFileSrc, croppedAreaPixels);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setCroppedBlob(blob);
      setPreviewUrl(URL.createObjectURL(blob));
      setShowCropper(false);
      setSelectedFileSrc(null);
    } catch (error) {
      toast.error("Could not process the selected image");
      console.log(error);
    }
  };

  const cancelCrop = () => {
    setShowCropper(false);
    setSelectedFileSrc(null);
  };

  const onSubmit = async (data) => {
    if (!isEditMode && !croppedBlob) {
      setImageError("Profile picture is required");
      return;
    }

    const formData = new FormData();
    formData.append("Name", data.name);
    formData.append("Position", data.position);
    if (data.displayOrder !== null && data.displayOrder !== undefined && data.displayOrder !== "") {
      formData.append("DisplayOrder", data.displayOrder);
    }
    formData.append("IsPublished", data.isPublished ? "true" : "false");
    if (croppedBlob) {
      formData.append("ImageFile", croppedBlob, "photo.jpg");
    }

    if (isEditMode) await updateAsync(formData);
    else await addAsync(formData);
  };

  const addAsync = async (formData) => {
    try {
      const result = await teamMemberService.create(formData);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to create team member");
        return;
      }
      toast.success("Team member added successfully");
      handleClear();
      await getAsync();
    } catch (error) {
      toast.error("An error occurred while creating the team member");
      console.log(error);
    }
  };

  const updateAsync = async (formData) => {
    try {
      const result = await teamMemberService.update(id, formData);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to update team member");
        return;
      }
      toast.success("Team member updated successfully");
      handleClear();
      await getAsync();
    } catch (error) {
      toast.error("An error occurred while updating the team member");
      console.log(error);
    }
  };

  const handleClear = () => {
    setId(0);
    reset(initialForm);
    resetImageState();
  };

  return (
    <section className="section-id-card-form">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Name"
                {...register("name")}
              />
              <small className="text-danger">{errors.name?.message}</small>
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label>Position</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Position"
                {...register("position")}
              />
              <small className="text-danger">{errors.position?.message}</small>
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label>Display Order</label>
              <input
                type="number"
                className="form-control"
                placeholder="Leave blank to add at the end"
                {...register("displayOrder")}
              />
              <small className="text-danger">{errors.displayOrder?.message}</small>
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group">
              <label className="d-block">Published</label>
              <div className="form-check form-switch">
                <input
                  type="checkbox"
                  className="form-check-input"
                  role="switch"
                  {...register("isPublished")}
                />
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="form-group">
              <label>Profile Picture</label>
              <input
                type="file"
                className="form-control"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileChange}
              />
              <small className="text-danger">{imageError}</small>

              {(previewUrl || existingImageUrl) && !showCropper && (
                <div className="mt-2">
                  <img
                    src={previewUrl || existingImageUrl}
                    alt="Preview"
                    style={{
                    width: 220,
                    height: 220,
                    objectFit: "cover",
                    borderRadius: "8px",
                    display: "block",
                  }}
                  />
                </div>
              )}

              {showCropper && selectedFileSrc && (
                <div className="mt-2">
                  <div style={{ position: "relative", width: "100%", height: 300, background: "#333" }}>
                    <Cropper
                      image={selectedFileSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={1}
                      cropShape="round"
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  </div>
                  <div className="d-flex align-items-center gap-2 mt-2">
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.1}
                      value={zoom}
                      onChange={(e) => setZoom(Number(e.target.value))}
                      className="form-range"
                      style={{ maxWidth: 200 }}
                    />
                    <button type="button" className="my-button" onClick={applyCrop}>
                      Apply Crop
                    </button>
                    <button type="button" className="my-button bg-secondary" onClick={cancelCrop}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="col-12">
            <div className="form-group flex-end">
              <button
                type="button"
                className="my-button bg-secondary mx-2"
                onClick={() => handleClear()}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="my-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </section>
  );
};

export default TeamMemberForm;
